'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Modal from '../modal/Modal';
import type { AnnouncementPopupData } from '@/app/api/announcement-popup/route';

const STORAGE_KEY = 'trusti_announcement_dismissed';

export default function AnnouncementPopup() {
  const [popupData, setPopupData] = useState<AnnouncementPopupData | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetch('/api/announcement-popup')
      .then(res => res.json())
      .then(({ data }: { data: AnnouncementPopupData }) => {
        if (!data?.enabled) return;

        const dismissedVersion = localStorage.getItem(STORAGE_KEY);
        if (dismissedVersion && Number(dismissedVersion) >= data.version)
          return;

        setPopupData(data);
        setIsOpen(true);
      })
      .catch(() => {
        // Silently fail - popup is non-critical
      });
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    if (popupData) {
      localStorage.setItem(STORAGE_KEY, String(popupData.version));
    }
  };

  if (!popupData) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="flex flex-col items-center gap-6 text-center">
        {popupData.imageUrl && (
          <div className="relative h-48 w-full overflow-hidden rounded-lg">
            <Image
              src={popupData.imageUrl}
              alt={popupData.heading || 'Announcement'}
              fill
              className="object-cover"
            />
          </div>
        )}

        {popupData.heading && (
          <h2 className="font-[family-name:var(--font-crimson-pro)] text-2xl font-bold text-[#1a1a1a] md:text-3xl">
            {popupData.heading}
          </h2>
        )}

        {popupData.body && (
          <p className="max-w-sm font-[family-name:var(--font-work-sans)] text-base leading-relaxed text-[#4a4a4a]">
            {popupData.body}
          </p>
        )}

        {popupData.buttonText && popupData.buttonUrl && (
          <a
            href={popupData.buttonUrl}
            className="inline-block rounded-full bg-gradient-to-r from-[#d64718] to-[#8875d5] px-8 py-3 font-[family-name:var(--font-work-sans)] text-base font-medium text-white transition-opacity hover:opacity-90"
          >
            {popupData.buttonText}
          </a>
        )}
      </div>
    </Modal>
  );
}
